import { FC, ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { debounce } from "@commons-monorepo/index";
import styles from "./ui-carousel.module.css";

interface UiCarouselProps {
  hideDots?: boolean;
  withArrows?: boolean;
  position?: number;
  className?: string;
  classNameContent?: string;
  children?: ReactNode;
  onChange?: (position: number) => void;
}

const UiCarousel: FC<UiCarouselProps> = ({
  position = 0,
  children,
  withArrows,
  hideDots,
  className,
  classNameContent,
  onChange
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [itemQuantity, setItemQuantity] = useState(0);
  const [dotActive, setDotActive] = useState(position);

  const onScroll = (e: any) => {
    const { scrollWidth, scrollLeft } = e.target;
    const index = Math.round(scrollLeft / scrollWidth * itemQuantity);
    if (index !== dotActive) {
      setDotActive(index);
      onChange && onChange(index);
    }
  };

  const handleCarouselScroll = debounce(onScroll, 20);
  const handleDotClick = useCallback(
    (index: number) => {
      if (contentRef.current) {
        const { scrollWidth } = contentRef.current;
        const itemWidth = scrollWidth / itemQuantity;
        contentRef.current?.scrollTo({
          left: itemWidth * index,
          behavior: "smooth"
        });
      }
    },
    [itemQuantity]
  );
  const handleArrowClick = (direction: "left" | "right") => {
    if (contentRef.current) {
      const { scrollWidth, scrollLeft } = contentRef.current;
      let index = Math.round(scrollLeft / scrollWidth * itemQuantity);
      index = direction === "left" ? index - 1 : index + 1;
      if (index !== dotActive) {
        handleDotClick(index);
        onChange && onChange(index);
      }
    }
  };

  useEffect(() => {
    if (contentRef?.current)
    setItemQuantity(contentRef.current.childElementCount);
  }, [contentRef, children]);

  useEffect(() => {
    handleDotClick(position);
  }, [position, handleDotClick]);

  return (
    <div
      className={`${styles.uiCarouselWrapper} ${className}`}
      onScrollCapture={handleCarouselScroll}>

      <div ref={contentRef} className={`${classNameContent} ${styles.content}`}>
        {children}
      </div>
      {withArrows &&
      <>
          {dotActive !== 0 &&
        <div
          className={`${styles.arrow} ${styles.left}`}
          onClick={() => handleArrowClick("left")}>

              <div className={styles.icon}>
                <span className='icon-arrow' />
              </div>
            </div>}

          {dotActive !== itemQuantity - 1 &&
        <div
          className={`${styles.arrow} ${styles.right}`}
          onClick={() => handleArrowClick("right")}>

              <div className={styles.icon}>
                <span className='icon-arrow' />
              </div>
            </div>}

        </>}

      {!hideDots && !!itemQuantity &&
      <div className={styles.dots}>
          {new Array(itemQuantity).fill(0).map((_, index) =>
        <div
          key={`ui-carousel-dot-${index}`}
          className={`${styles.dot} ${
          dotActive === index && styles.active
          }`}
          onClick={() => handleDotClick(index)} />

        )}
        </div>}

    </div>);

};

export default UiCarousel;